import React, { useEffect, useState } from 'react'
import { useChannelsAvailability, useCallMe, usePhone, useEmergencyCheck } from '@vp/contact-channels'
import {
  Banner,
  BannerTextContainer,
  BannerText,
  BannerEyebrow,
  BannerTitle,
  BannerDescription,
  BannerLinks,
  BannerImageContainer,
  BannerImage,
  FluidImage,
  Button,
  FlexBox,
  Icon,
  Box,
  Typography,
} from '@vp/swan'
import { useTranslation } from 'react-i18next'
import { CallMeWidget } from '@vp/care-react-c2c'
import { ContactChannelsProvider, EmailModal } from '@vp/contact-channels'
import { useContactUs } from '../../hooks/useContactUs'
import { CallMeButton } from './CallMeButton'
import { useIdentity, useLogger } from '@vp/ubik-context'
import { AssetStoreProvider, useAssetStore } from '../../contexts/AssetStoreProvider'
import { dayMappings } from '../../utils/dayNameMappings'
import './ContactUs.scss'

const NO_CHAT_LOCALES = ['en-in', 'pt-pt']
const NO_C2C_LOCALES = ['fr-fr', 'de-de', 'nl-be', 'fr-be', 'es-es', 'en-nz', 'en-sg']

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const checkIsMobile = () => {
      const width = window.innerWidth
      setIsMobile(width <= 767)
    }
    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])
  return isMobile
}

const formatPhoneSchedule = (
  schedule: Array<{
    startDay: string
    endDay: string
    startTime: string
    endTime: string
  }>,
  timezone: string,
  locale: string,
  separator: string = 'to',
  prefix: string = '',
) => {
  if (!schedule || schedule.length === 0) return []

  const localeLower = locale.toLowerCase()
  const localeConfig = dayMappings[localeLower]

  if (!localeConfig) return []

  const getFullDayName = (day: string) => localeConfig.days[day] || day

  const capitalizeFirst = (str: string) => {
    if (!str) return str
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  return schedule.map((item) => {
    let startDayFull = getFullDayName(item.startDay)
    let endDayFull = getFullDayName(item.endDay)

    const capitalizationRule = localeConfig.capitalize

    if (capitalizationRule === 'both') {
      startDayFull = capitalizeFirst(startDayFull)
      endDayFull = capitalizeFirst(endDayFull)
    } else if (capitalizationRule === 'first') {
      startDayFull = capitalizeFirst(startDayFull)
    }

    const days = item.startDay === item.endDay ? `${prefix}${startDayFull}` : `${prefix}${startDayFull} ${separator} ${endDayFull}`
    const hours = `${item.startTime} - ${item.endTime} ${timezone}`

    return {
      days,
      hours,
    }
  })
}

interface ContactUsProps {
  locale: string
  tenant: string
  articleId: string
}

interface ContactUsContentProps extends Omit<ContactUsProps, 'config'> {
  identity:
    | {
        isSignedIn: boolean
        authorizationHeader?: string
      }
    | undefined
}

const ContactUsContent: React.FC<ContactUsContentProps> = ({ locale, tenant, identity, articleId }) => {
  const lng = locale.toLowerCase().replace('-', '_')
  const { t } = useTranslation(tenant)
  const logger = useLogger()
  const isMobile = useIsMobile()
  const assetStore = useAssetStore()

  const generatePhoneLink = (phoneNumber: string) => {
    if (!isMobile) {
      return (
        <Typography component="span" fontSize="large" fontWeight="bold">
          {phoneNumber}
        </Typography>
      )
    }

    return (
      <a href={`tel:${phoneNumber}`} style={{ textDecoration: 'none', color: 'inherit' }}>
        <Typography component="span" fontSize="large" fontWeight="bold">
          {phoneNumber}
        </Typography>
      </a>
    )
  }

  const { handleChatClick, handleEmailClick, handleEmailModalClose, handleEmailSubmit, isLoading, isEmailModalOpen, isChatActive, chatStatus } = useContactUs()

  const [shouldShowChat, setShouldShowChat] = React.useState(false)
  const [shouldShowC2C, setShouldShowC2C] = React.useState(false)

  useEffect(() => {
    setShouldShowChat(!NO_CHAT_LOCALES.includes(locale))
    setShouldShowC2C(!NO_C2C_LOCALES.includes(locale))
  }, [locale])

  // Integrate C2C, chat, and email availability check
  const { channelInfo: channelAvailability, isLoading: isChannelsLoading } = useChannelsAvailability(['callMe', 'chat', 'email'])
  const callMeHook = useCallMe()
  const phoneHook = usePhone()
  const isC2CAvailable = channelAvailability?.callMe?.isAvailable
  const isChatAvailable = channelAvailability?.chat?.isAvailable
  const isEmailAvailable = channelAvailability?.email?.isAvailable

  useEffect(() => {
    if (channelAvailability && !isChannelsLoading) {
      const unavailableChannels = Object.entries(channelAvailability)
        .filter(([, channel]) => !channel.isAvailable)
        .map(([name, channel]) => ({ name, reason: channel.reason }))

      if (unavailableChannels.length > 0) {
        logger.warn('Some contact channels are unavailable', {
          unavailableChannels,
          locale,
          tenant,
        })
      }
    }
  }, [channelAvailability, isChannelsLoading, logger, locale, tenant])

  const outOfBusinessHours = channelAvailability?.chat?.reason === 'OUT_OF_BUISNESS_HOURS' && channelAvailability?.callMe?.reason === 'OUT_OF_BUISNESS_HOURS'

  const availableChannels = [shouldShowChat && isChatAvailable && 'chat', shouldShowC2C && isC2CAvailable && 'callMe', isEmailAvailable && 'email'].filter(
    Boolean,
  )

  const onlyEmailAvailable = availableChannels.length === 1 && isEmailAvailable

  const shouldShowBusinessHours =
    ((channelAvailability?.chat?.reason === 'OUT_OF_BUISNESS_HOURS' || channelAvailability?.chat?.reason === 'DISABLED') &&
      (channelAvailability?.callMe?.reason === 'OUT_OF_BUISNESS_HOURS' || channelAvailability?.callMe?.reason === 'DISABLED') &&
      onlyEmailAvailable) ||
    (outOfBusinessHours && onlyEmailAvailable)

  const { data: emergencyData } = useEmergencyCheck()
  const isEmergency = emergencyData?.enabled || shouldShowBusinessHours || false
  const phoneSchedule = callMeHook?.data?.plainData
    ? {
        schedule: callMeHook.data.plainData.schedule,
        timezone: callMeHook.data.plainData.timezone,
      }
    : null

  const formattedWorkingHours = phoneSchedule
    ? formatPhoneSchedule(
        phoneSchedule.schedule,
        phoneSchedule.timezone,
        locale,
        t(`${tenant}:contact_us.day_separator`, { lng }),
        t(`${tenant}:contact_us.day_prefix`, { lng, defaultValue: '' }),
      )
    : []

  const phoneData = phoneHook?.data?.plainData
    ? {
        phoneNumber: phoneHook.data.plainData.phoneNumber,
        schedule: phoneHook.data.plainData.schedule,
      }
    : null

  const getBannerContent = () => {
    if (shouldShowBusinessHours) {
      return {
        description: t(`${tenant}:contact_us.description_offline`, { lng }) || "We're here to help! Reach out to us through our available channels.",
        imageAlt: 'Contact Us - Limited channels available',
      }
    } else {
      return {
        description: t(`${tenant}:contact_us.description`, { lng }),
        imageAlt: 'Contact Us - Multiple channels available',
      }
    }
  }

  const bannerContent = getBannerContent()

  return (
    <>
      <Banner variant="full-width-image" textBoxHorizontalAlign="left" p={6}>
        <BannerTextContainer>
          <BannerText>
            <BannerEyebrow>{isEmergency ? t(`${tenant}:contact_us.subtitle_emergency`, { lng }) : t(`${tenant}:contact_us.subtitle`, { lng })}</BannerEyebrow>
            <BannerTitle>{t(`${tenant}:contact_us.title`, { lng })}</BannerTitle>
            <BannerDescription>
              <p>{bannerContent.description}</p>
              {shouldShowBusinessHours && (
                <Box>
                  {formattedWorkingHours.map((workingHour, index) => (
                    <FlexBox key={index} justifyContent="start" gap="10" alignItems="center">
                      <Box>{workingHour.days}</Box>
                      <Box>{workingHour.hours}</Box>
                    </FlexBox>
                  ))}
                </Box>
              )}
            </BannerDescription>
            <BannerLinks>
              <FlexBox gap="3" flexDirection="row" flexWrap="wrap">
                {shouldShowChat && (
                  <Button
                    skin="primary"
                    onClick={handleChatClick}
                    aria-label="Start a chat conversation"
                    disabled={isChannelsLoading || !isChatAvailable || isChatActive}
                    iconPosition="left"
                  >
                    <FlexBox flexDirection="row" alignItems="center" gap="2">
                      <Icon iconType="liveChat" size="standard" skin="white" />
                      {isChannelsLoading
                        ? t(`${tenant}:contact_us.button_states.loading`, { lng })
                        : chatStatus === 'chatting'
                          ? t(`${tenant}:contact_us.button_states.chat_active`, { lng })
                          : isLoading
                            ? t(`${tenant}:contact_us.button_states.starting_chat`, { lng })
                            : t(`${tenant}:contact_us.channels.chat`, { lng })}
                    </FlexBox>
                  </Button>
                )}

                {shouldShowC2C && (
                  <CallMeWidget
                    tenant={tenant}
                    locale={locale}
                    pageName={`Help Center Article:${articleId}`}
                    pageStage="Customer Care"
                    pageSection="Help Center:Article Page"
                    origin="Help-Center-C2C"
                    isSmokeTest={false}
                    isWix={false}
                    isHelpPanel={false}
                    formType={'embedded'}
                  >
                    <CallMeButton tenant={tenant} locale={locale} disabled={isChannelsLoading || !isC2CAvailable} isLoading={isChannelsLoading} />
                  </CallMeWidget>
                )}

                <Button
                  skin="secondary"
                  onClick={handleEmailClick}
                  aria-label="Send an email"
                  disabled={isChannelsLoading || !isEmailAvailable}
                  iconPosition="left"
                >
                  <FlexBox flexDirection="row" alignItems="start" gap="2">
                    <Icon iconType="email" size="standard" skin="standard" />
                    {isChannelsLoading ? t(`${tenant}:contact_us.button_states.loading`, { lng }) : t(`${tenant}:contact_us.channels.email`, { lng })}
                  </FlexBox>
                </Button>

                {!shouldShowC2C && phoneData?.phoneNumber && (
                  <FlexBox flexDirection="row" alignItems="center" gap="2">
                    <Icon iconType="phone" size="standard" skin="standard" />
                    {generatePhoneLink(phoneData.phoneNumber)}
                  </FlexBox>
                )}
              </FlexBox>
            </BannerLinks>
          </BannerText>
        </BannerTextContainer>
        <BannerImageContainer>
          <BannerImage imageFocalPoint="right top">
            <FluidImage
              src="https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto/help_center/SSH/contact-banner-image.png"
              alt={bannerContent.imageAlt}
            />
          </BannerImage>
        </BannerImageContainer>
      </Banner>

      {isEmailModalOpen && (
        <EmailModal
          tenant={tenant}
          locale={locale}
          contactChannels={{
            email: {
              slaMsg: t(`${tenant}:contact_us.sla_message`, { lng }),
              formMsg: t(`${tenant}:contact_us.form_message`, { lng }),
            },
          }}
          proadvantageType=""
          isModalOpen={isEmailModalOpen}
          closeModal={handleEmailModalClose}
          auth={{ identity }}
          assetStore={assetStore || undefined}
          onSubmit={handleEmailSubmit}
        />
      )}
    </>
  )
}

export const ContactUs: React.FC<ContactUsProps> = ({ locale, tenant, articleId }) => {
  const { auth, identity } = useIdentity()

  return (
    <ContactChannelsProvider
      locale={locale}
      tenant={tenant}
      origin="Help-Center-Article-Fragment"
      prefetchQueries={true}
      callMeConfig={{ allowVPNs: true }}
      auth={auth}
      useRecaptcha={true}
    >
      <AssetStoreProvider auth={auth}>
        <ContactUsContent locale={locale} tenant={tenant} identity={identity} articleId={articleId} />
      </AssetStoreProvider>
    </ContactChannelsProvider>
  )
}
